import { Cell, ColumnDef, createColumnHelper } from "@tanstack/react-table";
import merge from "lodash/merge";
import { Fragment } from "react/jsx-runtime";

import { CountingProgressBar } from "@/components/CountingProgressBar";
import { FormatDate } from "@/components/Date";
import { ErrorSpan } from "@/components/ErrorSpan";
import { SOSEntityHashLink } from "@/components/SosEntityHashLink";
import { SOSEntityLineItemHashLink } from "@/components/SosEntityLineItemHashLink";
import { SosEntityLink } from "@/components/SosEntityLink";
import { formatDineroCad } from "@/utils/dinero-util";
import { getCountingBarSortFunction } from "@/utils/react-table-utils";
import { PurchaseOrder, PurchaseOrderLineItem } from "../../queries/tracking-data/purchase-orders";
import { getCostTaxBreakdownColumnDefs } from "../CostTaxBreakdownColumns";
import { JobTrackingInternalLink } from "../JobTrackingInternalLink";
import { LineItemDescription } from "../LineItemDescription";

export interface PurchaseOrderTableRow {
  rowId: string;
  po: PurchaseOrder;
  line: PurchaseOrderLineItem;
}

// For po columns, set the rowspan of each cell on the on the first line to the total number of lines. Hide all subsequent rows for the same receipt
const poColCellProps = (
  cell: Cell<PurchaseOrderTableRow, unknown>,
  additionalProps?: Omit<React.TdHTMLAttributes<HTMLTableCellElement>, "rowSpan">,
) => {
  const { po, line } = cell.row.original;
  const firstLineId = po.jobLines[0]!.id;
  const isFirstLine = line.id === firstLineId;
  return isFirstLine ?
      { ...additionalProps, rowSpan: po.jobLines.length }
    : { ...additionalProps, className: "d-none " + (additionalProps?.className ?? "") };
};

// Create helpers for defining columns
const columnHelper = createColumnHelper<PurchaseOrderTableRow>();
const poAccessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ meta: { cellProps: poColCellProps } }, column));
const lineItemAccessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ enableSorting: false }, column));

const poColumns = [
  poAccessorCol("po.number", {
    header: "PO #",
    cell: ({ getValue, row }) => <SosEntityLink type="purchaseorder" id={row.original.po.id} label={getValue()} />,
  }),
  poAccessorCol("po.date", {
    header: "Date",
    cell: ({ getValue }) => <FormatDate date={getValue()} />,
  }),
  poAccessorCol("po.starEmoji", {
    header: "Star",
    meta: { cellProps: (cell) => poColCellProps(cell, { className: "text-center" }) },
  }),
  poAccessorCol("po.closed", {
    header: "Closed",
    cell: ({ getValue }) => (getValue() ? "✅" : "⬜"),
    meta: { cellProps: (cell) => poColCellProps(cell, { className: "text-center" }) },
  }),
  poAccessorCol("po.vendor.name", {
    header: "Vendor",
    cell: ({ getValue, row: { original } }) => (
      <>
        {getValue()} ({original.po.vendor.currency?.code ?? <ErrorSpan>⚠ No currency set</ErrorSpan>})
      </>
    ),
  }),
  poAccessorCol("po.receivedJobLines", {
    header: "Received Lines",
    sortingFn: getCountingBarSortFunction<PurchaseOrderTableRow>(
      ({ po }) => po.receivedJobLines,
      ({ po }) => po.jobLines.length,
    ),
    cell: ({ getValue, row }) => <CountingProgressBar now={getValue()} max={row.original.po.jobLines.length} />,
  }),
  poAccessorCol<"po.linkedReceipts", PurchaseOrder["linkedReceipts"]>("po.linkedReceipts", {
    header: "Linked Item Receipts",
    enableSorting: false,
    cell: ({ row, getValue }) =>
      getValue().map((receipt, index) => (
        <Fragment key={receipt.id}>
          {index > 0 && ", "}
          <JobTrackingInternalLink trackerPath="landed-costs">
            {({ targetPath, targetPageTitle }) => (
              <SOSEntityLineItemHashLink
                fromHash={row.id}
                urlPath={targetPath}
                tooltipContent={`View on '${targetPageTitle}' page`}
                type="itemreceipt"
                id={receipt.id}
                lineNumber={0}
                label={receipt.number}
              />
            )}
          </JobTrackingInternalLink>
        </Fragment>
      )),
  }),
  poAccessorCol("po.currency.code", {
    header: "Currency",
    cell: ({ getValue, row: { original } }) => {
      const poCurrencyCode = getValue();
      const vendorCurrency = original.po.vendor.currency;
      return (
        <>
          {poCurrencyCode}
          {vendorCurrency && poCurrencyCode !== vendorCurrency.code && <ErrorSpan>⚠ Mismatch With Vendor</ErrorSpan>}
        </>
      );
    },
  }),
  poAccessorCol("po.exchangeRate", { header: "Exchange Rate" }),
  poAccessorCol("po.jobLinesSummary.categorizedCosts.material.subtotal", {
    header: "Material Costs",
    cell: ({ getValue }) => formatDineroCad(getValue()),
  }),
  poAccessorCol("po.jobLinesSummary.categorizedCosts.nonMaterial.subtotal", {
    header: "Non-Material Costs",
    cell: ({ getValue }) => formatDineroCad(getValue()),
  }),
  poAccessorCol("po.jobLinesSummary.categorizedCosts.shipping.subtotal", {
    header: "Shipping Costs",
    cell: ({ getValue }) => formatDineroCad(getValue()),
  }),
  ...getCostTaxBreakdownColumnDefs<PurchaseOrderTableRow>(
    (row) => row.po.jobLinesSummary.cost,
    "po_jobLinesSummary_cost",
    {
      meta: {
        cellProps: poColCellProps,
      },
    },
  ),
];

const lineItemColumns = [
  lineItemAccessorCol("line.lineNumber", {
    header: "Line #",
    meta: {
      headerCellProps: { className: "border-start-double" },
      cellProps: { className: "border-start-double" },
    },
  }),
  lineItemAccessorCol("line.item.name", {
    header: "Item",
    cell: ({ row }) => (
      <JobTrackingInternalLink trackerPath="bom-by-component">
        {({ targetPath, targetPageTitle }) => (
          <SOSEntityHashLink
            fromHash={row.id}
            urlPath={targetPath}
            tooltipContent={`View on '${targetPageTitle}' page`}
            type="item"
            id={row.original.line.item.id}
            label={row.original.line.item.name}
          />
        )}
      </JobTrackingInternalLink>
    ),
  }),
  lineItemAccessorCol("line.item.type", { header: "Type", enableSorting: false }),
  lineItemAccessorCol("line.item.description", {
    header: "Description",
    cell: ({ row }) => <LineItemDescription line={row.original.line} />,
  }),
  lineItemAccessorCol("line.uomReceived", {
    header: "Received/Quantity",
    cell: ({ getValue, row }) => (
      <CountingProgressBar
        now={getValue()}
        max={row.original.line.uomQuantity}
        selectedItemUOM={row.original.line.itemUOM}
        baseUOM={row.original.line.item.baseItemUOM?.uom}
      />
    ),
  }),
  lineItemAccessorCol("line.linkedLineReceipts", {
    header: "Linked Item Receipts",
    cell: ({ row, getValue }) => (
      <>
        {getValue().map((receipt, index) => (
          <Fragment key={receipt.id}>
            {index > 0 && ", "}
            <JobTrackingInternalLink trackerPath="landed-costs">
              {({ targetPath, targetPageTitle }) => (
                <SOSEntityLineItemHashLink
                  fromHash={row.id}
                  urlPath={targetPath}
                  tooltipContent={`View on '${targetPageTitle}' page`}
                  type="itemreceipt"
                  id={receipt.id}
                  lineNumber={0}
                  label={receipt.number}
                />
              )}
            </JobTrackingInternalLink>
          </Fragment>
        ))}
      </>
    ),
  }),
  lineItemAccessorCol("line.unitCost.subtotal", {
    header: "Unit Cost",
    cell: ({ getValue }) => formatDineroCad(getValue()),
  }),
  ...getCostTaxBreakdownColumnDefs<PurchaseOrderTableRow>((row) => row.line.totalCost, "line_totalCost", {
    enableSorting: false,
  }),
];

// Export the full set of columns for the table
// NOTE: We have to define the group columns separately before this because the type inference for the group columns is broken in Tanstack Table
// See: https://github.com/TanStack/table/issues/5065
export const purchaseOrdersTableColumns: ColumnDef<PurchaseOrderTableRow>[] = [
  columnHelper.group({ header: "Purchase Orders", columns: poColumns }),
  columnHelper.group({
    header: "Line Items",
    columns: lineItemColumns,
    meta: { headerCellProps: { className: "border-start-double" } },
  }),
];
